import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { useAction, useQuery } from 'convex/react'
import { Camera, XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { openShareCard } from '@/lib/share-card'

const DISMISSED_KEY = 'gt:streak-story-dismissed'

function isDismissed(milestoneId: string): boolean {
  if (typeof localStorage === 'undefined') return false
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? '[]').includes(milestoneId)
  } catch {
    return false
  }
}

function dismiss(milestoneId: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    const current = JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? '[]')
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...new Set([...current, milestoneId])]))
  } catch {}
}

type Milestone = {
  _id: Id<'streakMilestones'>
  habitId?: Id<'nodes'> | undefined
  habitTitle?: string | undefined
  streakDays: number
}

// Opens the share-card sheet; posts straight to Instagram only when the provider is
// enabled server-side and the user's connected account can publish.
export function useShareStreakStory() {
  const { t } = useTranslation()
  const share = useAction(api.instagramStoriesNode.shareStreakToStory)
  const available = useQuery(api.apps.available)
  const connections = useQuery(api.apps.getConnectionStatus)
  const canPost =
    !!available?.instagram &&
    !!connections?.some((c) => c.provider === 'instagram' && c.status === 'active' && c.canPublish)
  return async (m: Milestone, closeFirst?: () => void): Promise<'posted' | 'manual' | 'failed'> => {
    if (canPost) {
      let res: Awaited<ReturnType<typeof share>> = 'error'
      try {
        res = await share({
          habitId: m.habitId ?? undefined,
          habitTitle: m.habitTitle ?? undefined,
          streakDays: m.streakDays,
          milestoneId: m._id,
        })
      } catch {}
      if (res === 'shared') {
        toast.success(t('streakStory.shared', { days: m.streakDays }))
        return 'posted'
      }
      if (res === 'rate_limited' || res === 'error') {
        toast.error(t(res === 'error' ? 'streakStory.error' : 'streakStory.rateLimited'))
        return 'failed'
      }
      if (res === 'reauth') toast(t('settings.apps.requiresReauth', { app: 'Instagram' }))
    }
    const open = () =>
      openShareCard(
        m.habitId
          ? { type: 'streak', ref: m.habitId, habit: m.habitId }
          : { type: 'streak', ref: 'all' },
      )
    if (closeFirst) {
      closeFirst()
      setTimeout(open, 200)
    } else open()
    return 'manual'
  }
}

export function StreakStoryPrompt() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [prompt, setPrompt] = useState<Milestone | null>(null)
  const unsharedMilestones = useQuery(
    api.instagramStories.getUnsharedStreakMilestones,
    open ? {} : 'skip',
  )
  const shareStreakStory = useShareStreakStory()

  useEffect(() => {
    if (!open) {
      const next = unsharedMilestones?.find((m) => !isDismissed(m._id))
      if (next) {
        setPrompt(next)
        setOpen(true)
      }
    }
  }, [open, unsharedMilestones])

  const shareStory = async () => {
    if (!prompt) return
    setOpen(false)
    if ((await shareStreakStory(prompt)) === 'manual') dismiss(prompt._id)
    setPrompt(null)
  }

  const maybeLater = () => {
    if (!prompt) return
    dismiss(prompt._id)
    setPrompt(null)
    setOpen(false)
  }

  if (!prompt || !open) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle className="font-semibold text-lg">{t('streakStory.title')}</DialogTitle>
        <div className="mt-4 flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            {t('streakStory.milestoneReached', { days: prompt.streakDays })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('streakStory.subtitle', { days: prompt.streakDays })}
          </p>
        </div>
        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={shareStory}
            className="flex w-1/2 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <Camera className="h-4 w-4" />
            {t('streakStory.button')}
          </button>
          <button
            type="button"
            onClick={maybeLater}
            className="flex w-1/2 items-center justify-center gap-2 rounded-xl border border-border px-4 py-3 text-sm text-muted-foreground transition-colors hover:bg-muted"
          >
            {t('common.maybeLater')}
            <XIcon className="h-4 w-4" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
